import {
  isContentfulLink,
  isGatsbyAsset,
  isLongTextField,
  isRecord,
  isRichTextDocument,
  isRichTextField,
  UnknownRecord,
} from './shared'

const fileHasUrl = (file: UnknownRecord): boolean => {
  if (typeof file.url === 'string' && file.url) return true
  if (isRecord(file.url)) {
    return Object.values(file.url).some(value => typeof value === 'string' && value)
  }

  return Object.values(file).some(value => isRecord(value) && typeof value.url === 'string' && value.url)
}

/**
 * Live-preview asset refs can look like plain objects (`{ title, file: { url } }`). Those must not
 * be treated as JSON Object fields.
 */
const isAssetLikeField = (field: UnknownRecord): boolean => {
  if (isRecord(field.sys) && (field.sys.linkType === 'Asset' || field.sys.type === 'Asset')) return true
  if (isRecord(field.file) && fileHasUrl(field.file)) return true

  for (const value of Object.values(field)) {
    if (isRecord(value) && isRecord(value.file) && fileHasUrl(value.file)) return true
  }

  return false
}

/**
 * Contentful Object/JSON fields arrive as plain objects on both sides — not links, entries, assets,
 * or the Long/Rich text wrappers the source plugin produces.
 */
export const overlayJson = (value: unknown, field: unknown): unknown | undefined => {
  if (
    !isRecord(field) ||
    isContentfulLink(field, 'Asset') ||
    isContentfulLink(field, 'Entry') ||
    isAssetLikeField(field) ||
    isRichTextDocument(field) ||
    !isRecord(value) ||
    typeof value.contentful_id === 'string' ||
    isGatsbyAsset(value) ||
    isLongTextField(value) ||
    isRichTextField(value)
  ) {
    return undefined
  }

  // gatsby-source-contentful turns a JSON field into a node: the object's keys plus `internal.content`, its JSON
  // string, which components such as GenericContentRenderer read. Keep that shape, or `internal` is lost.
  const { internal } = value as UnknownRecord
  if (isRecord(internal) && typeof internal.content === 'string') {
    return { ...field, internal: { ...internal, content: JSON.stringify(field) } }
  }

  return field
}
